// Saved Travellers — CRUD UI consuming /api/customer/travellers.
import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, X, User, Calendar, Flag, FileText } from 'lucide-react';
import { api } from '../../lib/api';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const BLANK = {
  full_name: '', gender: 'M', dob: '', nationality: 'IN',
  passport_number: '', passport_expiry: '', frequent_flyer: '',
};

const SavedTravellers = () => {
  const { customer, loading: authLoading } = useCustomerAuth();
  const [travellers, setTravellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | traveller
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const reload = () => {
    setLoading(true);
    api.get('/customer/travellers')
      .then((r) => setTravellers(r.data.travellers || []))
      .catch(() => setTravellers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!customer) { setLoading(false); return; }
    reload();
  }, [customer]);

  const openCreate = () => { setForm(BLANK); setEditing({}); };
  const openEdit = (t) => { setForm({ ...BLANK, ...t }); setEditing(t); };
  const close = () => setEditing(null);

  const save = async () => {
    if (!form.full_name || !form.dob) { toast.error('Name and Date of Birth are required.'); return; }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/customer/travellers/${editing.id}`, form);
        toast.success('Traveller updated.');
      } else {
        await api.post('/customer/travellers', form);
        toast.success('Traveller saved.');
      }
      close();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this traveller?')) return;
    try { await api.delete(`/customer/travellers/${id}`); toast.success('Traveller removed.'); reload(); }
    catch { toast.error('Could not remove.'); }
  };

  if (!authLoading && !customer) return <div className="p-10 text-center">Please sign in.</div>;
  if (loading) return <div className="p-10 text-center text-gray-500">Loading…</div>;

  return (
    <div className="bg-gray-50 dark:bg-gray-950 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Saved Travellers</h1>
          <Button onClick={openCreate} data-testid="add-traveller-btn" className="bg-red-600 hover:bg-red-700 text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add traveller
          </Button>
        </div>

        {travellers.length === 0 ? (
          <Card className="text-center py-12"><CardContent>
            <User className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <p className="font-semibold text-gray-900 dark:text-white">No saved travellers yet</p>
            <p className="text-sm text-gray-500 mt-1">Save passenger details once and we'll auto-fill them next time you book.</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="travellers-list">
            {travellers.map((t) => (
              <Card key={t.id} data-testid={`traveller-${t.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-bold text-gray-900 dark:text-white truncate">{t.full_name}</h3>
                      <p className="text-xs text-gray-500">{t.gender === 'F' ? 'Female' : t.gender === 'M' ? 'Male' : 'Other'} · DOB {t.dob}</p>
                    </div>
                    <div className="flex gap-1">
                      <button data-testid={`edit-traveller-${t.id}`} onClick={() => openEdit(t)} className="p-1.5 hover:bg-gray-100 rounded text-gray-600"><Edit2 className="h-4 w-4" /></button>
                      <button data-testid={`delete-traveller-${t.id}`} onClick={() => remove(t.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Badge variant="outline" className="text-[10px] flex items-center gap-1"><Flag className="h-3 w-3" /> {t.nationality || 'IN'}</Badge>
                    {t.passport_number && <Badge variant="outline" className="text-[10px] flex items-center gap-1"><FileText className="h-3 w-3" /> Passport</Badge>}
                    {t.frequent_flyer && <Badge variant="outline" className="text-[10px]">FF: {t.frequent_flyer}</Badge>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Modal */}
        {editing && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={close} data-testid="traveller-modal">
            <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
                <h2 className="font-bold text-gray-900 dark:text-white">{editing.id ? 'Edit traveller' : 'New traveller'}</h2>
                <button onClick={close} data-testid="close-traveller-modal" className="text-gray-500"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-4 space-y-3">
                <Input data-testid="traveller-name" placeholder="Full name (as on ID)" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <select data-testid="traveller-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="h-10 px-3 border border-gray-300 rounded-md text-sm">
                    <option value="M">Male</option><option value="F">Female</option><option value="O">Other</option>
                  </select>
                  <Input data-testid="traveller-dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
                </div>
                <Input data-testid="traveller-nationality" placeholder="Nationality (2-letter code, e.g. IN)" maxLength={2} value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value.toUpperCase() })} />
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="traveller-passport-number" placeholder="Passport number" value={form.passport_number} onChange={(e) => setForm({ ...form, passport_number: e.target.value.toUpperCase() })} />
                  <Input data-testid="traveller-passport-expiry" type="date" value={form.passport_expiry} onChange={(e) => setForm({ ...form, passport_expiry: e.target.value })} />
                </div>
                <Input data-testid="traveller-ff" placeholder="Frequent flyer number (optional)" value={form.frequent_flyer} onChange={(e) => setForm({ ...form, frequent_flyer: e.target.value })} />
              </div>
              <div className="p-4 border-t border-gray-100 dark:border-gray-800 flex gap-2 justify-end">
                <Button variant="outline" onClick={close}>Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="save-traveller" className="bg-red-600 hover:bg-red-700 text-white">{saving ? 'Saving…' : 'Save'}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedTravellers;
